import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumbs from "../Components/Breadcrumbs";
import Table from "../Components/Tables";
import Button from "../Components/Buttons";
import {UsersApiProvider, useUsersApi} from "../Services/UsersApiProvider";
import { AlertProvider, useAlert } from "../Components/AlertContext";
import { ConfirmationProvider, useConfirmation } from "../Components/ConfirmationContext";
import { formatDateUTC } from "../utils/formatDateUTC";

function UsersHub({ canEdit }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getUsers, deleteUser } = useUsersApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alertShown, setAlertShown] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert("success", location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const columns = [
    { name: "ID", selector: (row) => row.id, sortable: false },
    { name: "Name", selector: (row) => row.name, sortable: false },
    { name: "Email", selector: (row) => row.email, sortable: false },
    { name: "Status", selector: (row) => (row.is_active ? "Active" : "Inactive"), sortable: false },
    { name: "Created", selector: (row) => formatDateUTC(row.created_at), sortable: false },
    { name: "Updated", selector: (row) => formatDateUTC(row.updated_at), sortable: false }
  ];

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/users-hub/${row.id}`, {
      state: { organizationId }
    });
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;

    showConfirmation(
      `Are you sure you want to delete this user?`,
      async () => {
        try {
          setLoading(true);
          await deleteUser(selectedId);
          await fetchUsers(0, itemsPerPage);
          showAlert("success", "User deleted successfully!");
        } catch (error) {
          showAlert("fail", "Failed to delete user!");
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const fetchUsers = async (skip, limit) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getUsers(skip, limit);
      setUsers(response.items);
      setTotalUsers(response.totalCount);
    } catch (error) {
      setError(error.message);
      showAlert("fail", "Failed to fetch users!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Users"
                columns={columns}
                data={users}
                selectableRows
                selectableRowsHighlight
                paginationPerPage={itemsPerPage}
                progressPending={loading}
                paginationTotalRows={totalUsers}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchUsers}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/users-hub/new-user`)}
                >
                  Create New User
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function UsersHubWrapper({ canEdit }) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <UsersApiProvider>
          <UsersHub canEdit={canEdit} />
        </UsersApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default UsersHubWrapper;
